import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ProductCard } from '../components/ProductCard';
import { FilterSidebar } from '../components/FilterSidebar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { useAuth } from '../context/AuthContext';
import { EmailVerificationBanner } from '../components/EmailVerificationBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [sortBy, setSortBy] = useState('name');
  const [filters, setFilters] = useState({
    games: searchParams.get('game') ? [searchParams.get('game')] : [],
    categories: searchParams.get('category') ? [searchParams.get('category')] : [],
    rarities: [],
    promoOnly: false,
    preorderOnly: false,
    minPrice: 0,
    maxPrice: 200
  });

  // Sync filters with URL params when navigation changes
  const gameParam = searchParams.get('game');
  const categoryParam = searchParams.get('category');
  const searchParam = searchParams.get('search');

  useEffect(() => {
    setFilters({
      games: gameParam ? [gameParam] : [],
      categories: categoryParam ? [categoryParam] : [],
      rarities: [],
      minPrice: 0,
      maxPrice: 200
    });
    setSearchQuery(searchParam || '');
  }, [gameParam, categoryParam, searchParam]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (filters.games?.length === 1) {
        params.append('game', filters.games[0]);
      }
      if (filters.categories?.length === 1) {
        params.append('category', filters.categories[0]);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      if (sortBy) {
        params.append('sort', sortBy);
      }
      if (filters.minPrice > 0) {
        params.append('min_price', filters.minPrice);
      }
      if (filters.maxPrice < 200) {
        params.append('max_price', filters.maxPrice);
      }
      if (filters.rarities?.length === 1) {
        params.append('rarity', filters.rarities[0]);
      }

      const response = await axios.get(`${API}/products?${params.toString()}`);
      let filteredProducts = response.data.products;

      // Client-side filtering for multiple games/categories/rarities
      if (filters.games?.length > 1) {
        filteredProducts = filteredProducts.filter(p => filters.games.includes(p.game));
      }
      if (filters.categories?.length > 1) {
        filteredProducts = filteredProducts.filter(p => filters.categories.includes(p.category));
      }
      if (filters.rarities?.length > 1) {
        filteredProducts = filteredProducts.filter(p => filters.rarities.includes(p.rarity));
      }
      if (filters.promoOnly) {
        filteredProducts = filteredProducts.filter(p => p.discount_percent);
      }
      if (filters.preorderOnly) {
        filteredProducts = filteredProducts.filter(p => p.is_preorder);
      }

      // Sort: out of stock items at the end
      filteredProducts.sort((a, b) => {
        if (a.stock === 0 && b.stock > 0) return 1;
        if (a.stock > 0 && b.stock === 0) return -1;
        return 0;
      });

      setProducts(filteredProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, searchQuery, sortBy]);

  const fetchFavorites = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axios.get(`${API}/favorites`, { withCredentials: true });
      setFavorites(response.data.products.map(p => p.product_id));
    } catch {
      setFavorites([]);
    }
  }, [user]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (searchQuery) {
      newParams.set('search', searchQuery);
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({ games: [], categories: [], rarities: [], promoOnly: false, preorderOnly: false, minPrice: 0, maxPrice: 200 });
    setSearchQuery('');
    setSearchParams({});
  };

  const getPageTitle = () => {
    if (filters.games?.length === 1) {
      const gameLabels = { yugioh: 'Yu-Gi-Oh!', magic: 'Magic: The Gathering', pokemon: 'Pokémon', accessory: 'Accessoires' };
      return gameLabels[filters.games[0]] || 'Boutique';
    }
    if (filters.categories?.length === 1) {
      const catLabels = { deck: 'Decks', booster: 'Boosters', single: 'Cartes à l\'unité' };
      return catLabels[filters.categories[0]] || 'Boutique';
    }
    if (searchQuery) {
      return `Recherche: "${searchQuery}"`;
    }
    return 'Boutique';
  };

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="shop-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Email Verification Banner */}
        {user && <div className="mb-6"><EmailVerificationBanner user={user} /></div>}
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white font-outfit">
              {getPageTitle()}
            </h1>
            <p className="text-gray-400 mt-1">
              {loading ? 'Chargement...' : `${products.length} produit${products.length > 1 ? 's' : ''}`}
            </p>
          </div>

          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                type="text"
                placeholder="Rechercher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                data-testid="shop-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    const newParams = new URLSearchParams(searchParams);
                    newParams.delete('search');
                    setSearchParams(newParams);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40 bg-white/5 border-white/10 text-white" data-testid="sort-select">
                <SelectValue placeholder="Trier par" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-white/10">
                <SelectItem value="name" className="text-white hover:bg-white/10">Nom A-Z</SelectItem>
                <SelectItem value="name_desc" className="text-white hover:bg-white/10">Nom Z-A</SelectItem>
                <SelectItem value="price_asc" className="text-white hover:bg-white/10">Prix croissant</SelectItem>
                <SelectItem value="price_desc" className="text-white hover:bg-white/10">Prix décroissant</SelectItem>
              </SelectContent>
            </Select>

            {/* Mobile Filter Toggle */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden border-white/10 text-white" data-testid="mobile-filter-btn">
                  <SlidersHorizontal className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-[#0a0a0a] border-white/10 w-80">
                <SheetHeader>
                  <SheetTitle className="text-white">Filtres</SheetTitle>
                </SheetHeader>
                <div className="mt-6">
                  <FilterSidebar 
                    filters={filters} 
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                  />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block">
            <FilterSidebar 
              filters={filters} 
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
            />
          </div>

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="bg-[#121212] rounded-xl overflow-hidden animate-pulse">
                    <div className="aspect-square bg-white/5" />
                    <div className="p-4 space-y-3">
                      <div className="h-4 bg-white/5 rounded" />
                      <div className="h-6 bg-white/5 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-gray-400 text-lg">Aucun produit trouvé</p>
                <Button
                  variant="outline"
                  className="mt-4 border-white/10 text-white"
                  onClick={handleClearFilters}
                >
                  Réinitialiser les filtres
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {products.map((product) => (
                  <ProductCard
                    key={product.product_id}
                    product={product}
                    isFavorite={favorites.includes(product.product_id)}
                    onFavoriteToggle={fetchFavorites}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
